/**
 * Letting the browser skip layout and paint for static elements that are off screen.
 *
 * The whole document is in the DOM as static html, so any mutation - and a swap makes two - costs a
 * layout of all of it, which is far more than parsing the part being loaded.
 */

/** How many elements get measured per pass. */
const MEASURE_BATCH = 120;

/**
 * An element whose height can still change is left alone. An image that has not loaded yet lays out
 * at one height and at another once it has, and an element skipped at the first height reports that
 * height for as long as it is off screen - which is the page growing while you scroll through it.
 */
const hasSettled = (element: HTMLElement): boolean =>
  // A failed image keeps naturalHeight at 0 forever; `complete` turns true either way.
  Array.from(element.querySelectorAll('img')).every((image) => image.complete);

/**
 * Measure one batch and skip what is safe to skip. Returns true when it did work, so the caller can
 * come back on an idle frame instead of making this pass longer.
 *
 * `content-visibility: auto` needs to be told how tall a skipped element is. Guessing costs 25% of
 * the scroll height, so nothing is guessed: each element is measured at the size it actually
 * rendered, and only an element that has settled is skipped. One forced layout per pass, from
 * reading every pending height before writing any style.
 */
export const measureAndSkip = (container: HTMLElement): boolean => {
  const width = container.clientWidth;
  if (!width) return false;
  /**
   * Edit mode belongs in the key alongside the width.
   *
   * It draws guides the reader never sees - a dashed border on every borderless cell is 1px per row -
   * so a height remembered in one mode is wrong in the other, and an element skipped at the wrong
   * height reports it for as long as it stays off screen.
   */
  const measuredAt = `${width}:${container.closest('.edit-mode') ? 'edit' : 'view'}`;

  /**
   * A slice per pass, and the cheap test first.
   *
   * A whole document at once is one long task, which is what makes the browser stop responding while
   * a document opens. `hasSettled` walks an element's images, so it must run on the batch, not on all
   * every candidate before the batch is taken. An element that has not had its turn renders normally.
   */
  const pending: HTMLElement[] = [];
  for (const child of Array.from(container.children) as HTMLElement[]) {
    if (pending.length >= MEASURE_BATCH) break;
    // The width each element was measured at is kept on the element: different width, different
    // wrapping, so a height measured at another width is worthless. Cleared as each one's turn comes,
    // because clearing all of them in one pass blocks the main thread on a large document.
    if (child.dataset.measuredWidth === measuredAt) continue;
    // Measured once and found to change height under containment - never a candidate again.
    if (child.dataset.skipUnsafe) continue;
    if (hasSettled(child)) pending.push(child);
  }
  if (!pending.length) return false;

  // A skipped element reports its intrinsic size, not its real height, so stop skipping before measuring.
  pending.forEach((element) => {
    element.style.contentVisibility = '';
    element.style.containIntrinsicSize = '';
  });
  // Read every height first, then write. Interleaving them makes the browser lay out per element.
  const heights = pending.map((element) => element.offsetHeight);
  pending.forEach((element, index) => {
    // An element that measures 0 is not laid out yet - leave it for a later pass.
    if (!heights[index]) return;
    element.style.containIntrinsicSize = `${heights[index]}px`;
    element.style.contentVisibility = 'auto';
  });
  /**
   * Containment must not change the height, or the two renderings disagree.
   *
   * `content-visibility: auto` applies layout containment even while the element is on screen, and
   * layout containment stops margins collapsing out of the element - so an element can render taller
   * here than in the editor, and jump as its part loads. Rather than
   * guess which elements are affected, check: read the height back, and give up the optimisation on
   * the ones it changes.
   */
  const contained = pending.map((element) => element.offsetHeight);
  pending.forEach((element, index) => {
    if (!heights[index]) return;
    if (Math.abs(contained[index] - heights[index]) > 0.5) {
      element.style.contentVisibility = '';
      element.style.containIntrinsicSize = '';
      element.dataset.skipUnsafe = '1';
    }
    element.dataset.measuredWidth = measuredAt;
  });
  return true;
};
